/* Drop-in replacement: checklist fields open in an accessible modal popup. */
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getValue } from "../../../utils";
import { FieldShell } from "../components/FieldShell";
import {
  decodeHtmlEntities,
  getBaseField,
  isEmpty,
  objectLabel,
} from "../utils/valueUtils";

const CHECKPOINT_KEYS = [
  "CheckPoints",
  "Checkpoints",
  "checkPoints",
  "CheckPoint",
  "checkpoint",
  "question",
  "label",
  "name",
  "description",
  "title",
];

const RESPONSE_KEYS = [
  "response",
  "Response",
  "defaultResponse",
  "answer",
  "Answer",
  "status",
  "result",
  "value",
  "selectedOption",
];

const CATEGORY_KEYS = ["title", "category", "section", "group"];

function firstDefined(source, keys) {
  if (!source || typeof source !== "object") return undefined;

  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }

  return undefined;
}

function getSubtableItems(row, column) {
  if (!row || !column) return [];

  const rawValue = getValue(row, column.field);
  if (Array.isArray(rawValue)) return rawValue;

  const baseValue = row?.[getBaseField(column.field)];
  if (Array.isArray(baseValue)) return baseValue;

  if (baseValue && typeof baseValue === "object") {
    const nestedCandidates = [
      baseValue.SafetyChecks,
      baseValue.safetyChecks,
      baseValue.safetyMeasuresTaken,
      baseValue.details,
      baseValue.items,
      baseValue.checks,
      baseValue.checklist,
    ];

    const nestedArray = nestedCandidates.find(Array.isArray);
    if (nestedArray) return nestedArray;
  }

  if (Array.isArray(row?.SafetyChecks)) return row.SafetyChecks;
  if (Array.isArray(row?.safetyChecks)) return row.safetyChecks;
  if (Array.isArray(row?.safetyMeasuresTaken)) return row.safetyMeasuresTaken;
  if (Array.isArray(row?.details)) return row.details;

  return [];
}

function normalizeResponse(value, fallback = "N/A") {
  const decoded = decodeHtmlEntities(String(value ?? fallback)).trim();
  return decoded || fallback;
}

function responseTone(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();

  if (
    ["yes", "true", "pass", "passed", "ok", "compliant"].includes(normalized)
  ) {
    return "yes";
  }

  if (["no", "false", "fail", "failed", "not compliant"].includes(normalized)) {
    return "no";
  }

  return "na";
}

function normalizeChecklistItem(item, itemIndex, fallbackResponse = "N/A") {
  if (item === null || item === undefined) {
    return {
      id: `checkpoint-${itemIndex}`,
      category: "",
      question: "—",
      response: fallbackResponse,
      tone: "na",
    };
  }

  if (typeof item !== "object") {
    const question = decodeHtmlEntities(String(item));

    return {
      id: `${question}-${itemIndex}`,
      category: "",
      question: question || "—",
      response: fallbackResponse,
      tone: responseTone(fallbackResponse),
    };
  }

  const rawQuestion = firstDefined(item, CHECKPOINT_KEYS);
  const rawCategory = firstDefined(item, CATEGORY_KEYS);
  const rawResponse = firstDefined(item, RESPONSE_KEYS);

  const question = decodeHtmlEntities(String(rawQuestion ?? "")).trim() || "—";
  const decodedCategory = decodeHtmlEntities(String(rawCategory ?? "")).trim();

  const category =
    decodedCategory && decodedCategory !== question ? decodedCategory : "";

  const response = normalizeResponse(rawResponse, fallbackResponse);

  return {
    id: item._id || item.id || `${question}-${itemIndex}`,
    category,
    question,
    response,
    tone: responseTone(response),
  };
}

function isChecklistData(items, column) {
  const type = String(column?.type || "").toLowerCase();

  if (type === "permitchecklists" || type === "safetychecks") return true;

  const subFields = Array.isArray(column?.subFields) ? column.subFields : [];

  const hasChecklistSubField = subFields.some((subField) =>
    ["permitchecklists", "safetychecks"].includes(
      String(subField?.type || "").toLowerCase(),
    ),
  );

  if (hasChecklistSubField) return true;

  const configuredFields = subFields.map((subField) =>
    String(subField?.field || "").toLowerCase(),
  );

  if (
    configuredFields.includes("response") &&
    configuredFields.some((field) =>
      ["checkpoints", "checkpoint", "question", "title"].includes(field),
    )
  ) {
    return true;
  }

  return items.some(
    (item) =>
      item &&
      typeof item === "object" &&
      RESPONSE_KEYS.some((key) => item[key] !== undefined) &&
      CHECKPOINT_KEYS.some((key) => item[key] !== undefined),
  );
}

function ChecklistList({ items, label, fallbackResponse = "N/A" }) {
  const checklistItems = items.map((item, itemIndex) =>
    normalizeChecklistItem(item, itemIndex, fallbackResponse),
  );

  const totals = checklistItems.reduce(
    (summary, item) => {
      summary[item.tone] += 1;
      return summary;
    },
    { yes: 0, no: 0, na: 0 },
  );

  return (
    <div className="safety-checklist" aria-label={`${label} checklist`}>
      <div className="safety-checklist__summary">
        <strong>
          {checklistItems.length} checkpoint
          {checklistItems.length === 1 ? "" : "s"}
        </strong>

        <div className="safety-checklist__summary-statuses">
          {totals.yes > 0 ? (
            <span className="safety-checklist__summary-chip safety-checklist__summary-chip--yes">
              Yes {totals.yes}
            </span>
          ) : null}

          {totals.no > 0 ? (
            <span className="safety-checklist__summary-chip safety-checklist__summary-chip--no">
              No {totals.no}
            </span>
          ) : null}

          {totals.na > 0 ? (
            <span className="safety-checklist__summary-chip safety-checklist__summary-chip--na">
              N/A {totals.na}
            </span>
          ) : null}
        </div>
      </div>

      <ol className="safety-checklist__list">
        {checklistItems.map((item, itemIndex) => (
          <li className="safety-checklist__item" key={item.id}>
            <span className="safety-checklist__number" aria-hidden="true">
              {String(itemIndex + 1).padStart(2, "0")}
            </span>

            <div className="safety-checklist__content">
              {item.category ? (
                <span className="safety-checklist__category">
                  {item.category}
                </span>
              ) : null}

              <p className="safety-checklist__question">{item.question}</p>
            </div>

            <span
              className={`safety-checklist__response safety-checklist__response--${item.tone}`}
              aria-label={`Response: ${item.response}`}
            >
              <span
                className="safety-checklist__response-dot"
                aria-hidden="true"
              />
              {item.response}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function ChecklistPopup({ open, setOpen, items, label, fallbackResponse }) {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;

    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [open, setOpen]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="safety-checklist-modal" onMouseDown={() => setOpen(false)}>
      <section
        className="safety-checklist-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-label={`${label} checklist details`}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="safety-checklist-modal__header">
          <div>
            <span className="safety-checklist-modal__eyebrow">
              Work permit checklist
            </span>
            <h3>{label}</h3>
          </div>

          <button
            ref={closeButtonRef}
            type="button"
            className="safety-checklist-modal__close"
            onClick={() => setOpen(false)}
            aria-label="Close checklist"
          >
            ×
          </button>
        </header>

        <div className="safety-checklist-modal__body">
          <ChecklistList
            items={items}
            label={label}
            fallbackResponse={fallbackResponse}
          />
        </div>
      </section>
    </div>,
    document.body,
  );
}

function ChecklistLauncher({ items, label, fallbackResponse = "N/A" }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="safety-checklist-launcher">
      <button
        type="button"
        className="safety-checklist-launcher__button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="safety-checklist-launcher__icon" aria-hidden="true">
          ✓
        </span>

        <span className="safety-checklist-launcher__content">
          <strong>View complete checklist</strong>
          <small>
            {items.length} checkpoint{items.length === 1 ? "" : "s"} and
            responses
          </small>
        </span>

        <span className="safety-checklist-launcher__arrow" aria-hidden="true">
          →
        </span>
      </button>

      <ChecklistPopup
        open={open}
        setOpen={setOpen}
        items={items}
        label={label}
        fallbackResponse={fallbackResponse}
      />
    </div>
  );
}

export function PermitChecklistField({ row, label, column }) {
  const items = getSubtableItems(row, column);
  const responseSubField = (column.subFields || []).find(
    (subField) => String(subField?.field || "").toLowerCase() === "response",
  );

  return (
    <FieldShell
      label={label}
      column={column}
      className="safety-row-details__field--subtable safety-row-details__field--checklist"
    >
      {items.length ? (
        <ChecklistLauncher
          items={items}
          label={label}
          fallbackResponse={responseSubField?.prefilled || "N/A"}
        />
      ) : (
        <span className="safety-row-details__dash">—</span>
      )}
    </FieldShell>
  );
}

export function GenericSubtableField({ row, label, column }) {
  const items = getSubtableItems(row, column);
  const subFields = Array.isArray(column.subFields) ? column.subFields : [];

  const responseSubField = subFields.find(
    (subField) => String(subField?.field || "").toLowerCase() === "response",
  );

  if (items.length && isChecklistData(items, column)) {
    return (
      <FieldShell
        label={label}
        column={column}
        className="safety-row-details__field--subtable safety-row-details__field--checklist"
      >
        <ChecklistLauncher
          items={items}
          label={label}
          fallbackResponse={responseSubField?.prefilled || "N/A"}
        />
      </FieldShell>
    );
  }

  return (
    <FieldShell
      label={label}
      column={column}
      className="safety-row-details__field--subtable"
    >
      {items.length && subFields.length ? (
        <div className="safety-row-details__subtable-scroll">
          <table className="safety-row-details__subtable">
            <thead>
              <tr>
                {subFields.map((subField) => (
                  <th key={subField.field}>
                    {subField.label || subField.field}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {items.map((item, itemIndex) => (
                <tr key={item?._id || item?.id || itemIndex}>
                  {subFields.map((subField) => {
                    const value = getValue(item, subField.field);
                    const type = String(subField.type || "").toLowerCase();

                    if (type === "imagelink" && typeof value === "string") {
                      return (
                        <td key={subField.field}>
                          {value ? (
                            <img
                              src={value}
                              alt={subField.label || subField.field}
                              className="safety-row-details__subtable-image"
                              onClick={() =>
                                window.open(
                                  value,
                                  "_blank",
                                  "noopener,noreferrer",
                                )
                              }
                              onError={(event) => {
                                event.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            "—"
                          )}
                        </td>
                      );
                    }

                    if (type === "dropdown") {
                      const displayValue = isEmpty(value)
                        ? subField.prefilled || "—"
                        : String(value);

                      return (
                        <td key={subField.field}>
                          <span className="safety-row-details__chip">
                            {displayValue}
                          </span>
                        </td>
                      );
                    }

                    if (value && typeof value === "object") {
                      return (
                        <td key={subField.field}>
                          {objectLabel(value, subField) || "—"}
                        </td>
                      );
                    }

                    return (
                      <td key={subField.field}>
                        {isEmpty(value)
                          ? "—"
                          : decodeHtmlEntities(String(value))}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <span className="safety-row-details__dash">—</span>
      )}
    </FieldShell>
  );
}
